import { IcPlus } from '@assets/icons/24x24';
import { Box, For, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { RootState } from '@redux/store';
import { sensorFamilyFromSensorType, SensorShorthand } from '@utils/sensors/configuration.constants';
import { emptySensorByType } from '@utils/sensors/sensors.constants';
import { isEditableType } from '@utils/sensors/sensors.utils';
import { FormattedMessage } from 'react-intl';

import { ActionEnum } from '@/types/commons/commons.types';
import { SensorOrSubSensorWithShorthand, SensorTypeWithShorthand } from '@/types/sensor/sensorInstallation.types';

import SensorCard from './SensorCard';
import { useSensorInstallationContext } from './SensorInstallationContext';

interface Props {
  type: SensorTypeWithShorthand;
  sensors: SensorOrSubSensorWithShorthand[];
  setOpen: (open: boolean) => void;
}

export default function SensorGroup({ type, sensors, setOpen }: Readonly<Props>) {
  const { setEditedSensor } = useSensorInstallationContext();
  const hasCreateSensorRole = useAppSelector((state: RootState) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.CREATE,
      sensorFamily: sensorFamilyFromSensorType[type],
    }),
  );

  return (
    <VStack key={type} width="100%" gap={0}>
      <HStack width="100%" height="48px" paddingLeft={2} backgroundColor="neutral.900">
        <Text color="neutral.200">{SensorShorthand[type]}</Text>
        <Text color="neutral.500">
          <FormattedMessage id={`sensors.type.${type}`} />
        </Text>
        {hasCreateSensorRole && isEditableType(type) && (
          <IconButton
            aria-label="Add"
            variant="transparent"
            boxSize="56px"
            marginLeft="auto"
            onClick={() => setEditedSensor(emptySensorByType[type])}
          >
            <Icon as={IcPlus} boxSize="24px" />
          </IconButton>
        )}
      </HStack>
      {sensors.length === 0 ? (
        <Box
          width="100%"
          height="56px"
          paddingLeft={5}
          display="flex"
          alignItems="center"
          backgroundColor="neutral.800"
        >
          <Text color="neutral.200">
            <FormattedMessage id="components.sensorInstallation.filters.sensors.noSensor" />
          </Text>
        </Box>
      ) : (
        <VStack width="100%" backgroundColor="neutral.900" gap={0.25}>
          <For each={sensors}>
            {(sensor) => <SensorCard key={sensor.uniqueKey} sensor={sensor} setOpen={setOpen} />}
          </For>
        </VStack>
      )}
    </VStack>
  );
}
